import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { products, categories } from '../data/mock';
import { useCart } from '../context/CartContext';
import { ShoppingBag, ChevronRight, Minus, Plus, CheckCircle2, Truck, Shield, RotateCcw, Music2, Sparkles, Wrench } from 'lucide-react';

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  const product = products.find(p => p.slug === slug);
  const category = product ? categories.find(c => c.id === product.categoryId) : null;
  const relatedProducts = product
    ? products.filter(p => p.categoryId === product.categoryId && p.id !== product.id).slice(0, 4)
    : [];

  useEffect(() => {
    if (product) {
      document.title = `${product.name} | Pitch Craft Gear`;
    }
    window.scrollTo(0, 0);
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-dark-50">
        <Header />
        <CartDrawer />
        <main>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
            <Music2 className="w-20 h-20 text-dark-200 mx-auto mb-6" />
            <h1 className="font-display text-3xl font-semibold text-dark-900 mb-4">Product Not Found</h1>
            <p className="text-dark-500 mb-8">The instrument you&apos;re looking for doesn&apos;t exist.</p>
            <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3 bg-dark-900 text-white font-semibold tracking-wide rounded-full hover:bg-athletic-600 transition-colors">Back to Shop</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleQuantityChange = (delta) => setQuantity(prev => Math.max(1, prev + delta));
  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  return (
    <div className="min-h-screen bg-dark-50">
      <Header />
      <CartDrawer />

      <main>
        {/* Breadcrumb */}
        <div className="bg-white border-b border-dark-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center gap-1.5 text-sm flex-wrap">
              <Link to="/" className="text-dark-500 hover:text-athletic-600">Home</Link>
              <ChevronRight className="w-4 h-4 text-dark-300" />
              <Link to="/shop" className="text-dark-500 hover:text-athletic-600">Shop</Link>
              <ChevronRight className="w-4 h-4 text-dark-300" />
              <Link to={`/shop?category=${category?.slug}`} className="text-dark-500 hover:text-athletic-600">{category?.name}</Link>
              <ChevronRight className="w-4 h-4 text-dark-300" />
              <span className="text-dark-900 font-medium truncate max-w-[220px]">{product.name}</span>
            </nav>
          </div>
        </div>

        {/* Product hero */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Image */}
            <div className="lg:sticky lg:top-28 self-start">
              <div className="relative aspect-square bg-white rounded-[2rem] overflow-hidden border border-dark-100 shadow-soft">
                <img src={product.image} alt={product.name} className="w-full h-full object-contain p-10" />
                {product.badge && (
                  <span className="absolute top-4 left-4 px-3 py-1.5 bg-athletic-500 text-white text-xs font-semibold tracking-wider rounded-full">{product.badge}</span>
                )}
              </div>
            </div>

            {/* Info + purchase */}
            <div>
              <Link to={`/shop?category=${category?.slug}`} className="inline-flex items-center gap-2 text-athletic-700 text-sm font-semibold uppercase tracking-[0.15em] hover:text-athletic-800 transition-colors mb-3">
                <Music2 className="w-4 h-4" /> {category?.name}
              </Link>
              <h1 className="font-display text-3xl lg:text-4xl font-semibold text-dark-900 mb-4 tracking-tight">{product.name}</h1>

              <div className="flex items-center gap-4 mb-6">
                <span className="text-4xl font-semibold text-athletic-600 font-display">${product.price.toFixed(2)}</span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-power-50 text-power-700 text-sm font-semibold rounded-full">
                  <CheckCircle2 className="w-4 h-4" /> In Stock
                </span>
              </div>

              <p className="text-dark-600 text-lg leading-relaxed mb-8">{product.description}</p>

              {/* Purchase box */}
              <div className="bg-white border border-dark-100 rounded-2xl p-6 mb-6 shadow-soft">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex items-center bg-dark-50 border border-dark-200 rounded-full overflow-hidden">
                    <button onClick={() => handleQuantityChange(-1)} className="p-3 text-dark-600 hover:text-athletic-600 transition-colors" aria-label="Decrease quantity"><Minus className="w-5 h-5" /></button>
                    <span className="w-14 text-center text-dark-900 font-semibold text-lg">{quantity}</span>
                    <button onClick={() => handleQuantityChange(1)} className="p-3 text-dark-600 hover:text-athletic-600 transition-colors" aria-label="Increase quantity"><Plus className="w-5 h-5" /></button>
                  </div>
                  <button
                    onClick={handleAddToCart}
                    className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 font-semibold tracking-wide rounded-full transition-all shadow-button ${addedToCart ? 'bg-power-600 text-white' : 'bg-athletic-500 hover:bg-athletic-600 text-white'}`}
                  >
                    {addedToCart ? (<><CheckCircle2 className="w-5 h-5" /> Added to Cart!</>) : (<><ShoppingBag className="w-5 h-5" /> Add to Cart</>)}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-6">
                  <div className="flex flex-col items-center text-center">
                    <Truck className="w-5 h-5 text-athletic-600 mb-1.5" />
                    <span className="text-dark-900 text-xs font-semibold">Free Shipping</span>
                    <span className="text-dark-400 text-[11px]">USPS, 2-4 days</span>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <Shield className="w-5 h-5 text-athletic-600 mb-1.5" />
                    <span className="text-dark-900 text-xs font-semibold">Secure Checkout</span>
                    <span className="text-dark-400 text-[11px]">SSL Protected</span>
                  </div>
                  <div className="flex flex-col items-center text-center">
                    <RotateCcw className="w-5 h-5 text-athletic-600 mb-1.5" />
                    <span className="text-dark-900 text-xs font-semibold">30-Day Returns</span>
                    <span className="text-dark-400 text-[11px]">Hassle-free</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Full-width detail sections */}
          <div className="mt-14 grid lg:grid-cols-3 gap-8 border-t border-dark-100 pt-12">
            {product.keyBenefits && (
              <div className="bg-white border border-dark-100 rounded-2xl p-6 shadow-soft">
                <h3 className="flex items-center gap-2 font-display font-semibold text-dark-900 mb-4"><Sparkles className="w-4 h-4 text-athletic-600" /> Highlights</h3>
                <ul className="space-y-3">
                  {product.keyBenefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-2.5"><CheckCircle2 className="w-5 h-5 text-athletic-600 mt-0.5 flex-shrink-0" /><span className="text-dark-700 text-sm">{benefit}</span></li>
                  ))}
                </ul>
              </div>
            )}
            {product.usage && (
              <div className="bg-white border border-dark-100 rounded-2xl p-6 shadow-soft">
                <h3 className="flex items-center gap-2 font-display font-semibold text-dark-900 mb-4"><Wrench className="w-4 h-4 text-athletic-600" /> How to use</h3>
                <p className="text-dark-600 text-sm leading-relaxed">{product.usage}</p>
              </div>
            )}
            {product.features && (
              <div className="bg-white border border-dark-100 rounded-2xl p-6 shadow-soft">
                <h3 className="font-display font-semibold text-dark-900 mb-4">At a glance</h3>
                <div className="flex flex-wrap gap-2">
                  {product.features.map((feature, i) => (
                    <span key={i} className="px-3 py-1.5 bg-dark-50 text-dark-700 text-sm rounded-full border border-dark-200">{feature}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Related */}
          {relatedProducts.length > 0 && (
            <div className="mt-20">
              <h2 className="font-display text-2xl font-semibold text-dark-900 mb-8 tracking-tight">You may also like</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedProducts.map(rel => (<ProductCard key={rel.id} product={rel} />))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
